import React, { useEffect, useMemo, useState } from "react";
import "../styles/LeavesPage.css";
import { useI18n } from "../providers/I18nProvider";
import LeaveForm from "../components/LeaveForm";
import LeaveDetailsModal from "../components/LeaveDetailsModal";
import PaginationControls from "../components/PaginationControls";
import NoirAlert from "../components/NoirAlert";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../providers/ConfirmProvider";
import {
  TabBar,
  FilterBar,
  SearchInput,
  FilterField,
  EmptyState,
  countBy,
} from "../components/ListToolbar";

const parseDateOnly = (dateStr) => new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);

const businessDaysBetweenInclusive = (startDate, endDate) => {
  let count = 0;
  const current = new Date(startDate.getTime());
  while (current <= endDate) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

const pad2 = (n) => String(n).padStart(2, "0");
const localKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Hafta içine denk gelen resmi tatiller iş günü sayılmaz (backend hesabıyla tutarlı).
const overlapBusinessDays = (leaveStart, leaveEnd, windowStart, windowEnd, holidayKeys) => {
  const start = new Date(Math.max(leaveStart.getTime(), windowStart.getTime()));
  const end = new Date(Math.min(leaveEnd.getTime(), windowEnd.getTime()));
  if (start > end) return 0;
  let total = businessDaysBetweenInclusive(start, end);
  if (holidayKeys && holidayKeys.size > 0) {
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6 && holidayKeys.has(localKey(cur))) total -= 1;
      cur.setDate(cur.getDate() + 1);
    }
  }
  return Math.max(0, total);
};

const OWNER_EDIT_PAST_DAYS = 3;

const statusKey = (status) => String(status || "").trim().toLocaleLowerCase("tr-TR");
const isPendingStatus = (st) => statusKey(st) === "beklemede";
const isApprovedStatus = (st) => statusKey(st) === "onaylandı";
const isClosedStatus = (st) => {
  const k = statusKey(st);
  return k === "reddedildi" || k === "iptal edildi" || k === "i̇ptal edildi";
};
const dateKey = (v) => (v ? String(v).slice(0, 10) : "");

const LeavesPage = () => {
  const confirm = useConfirm();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const loadedOnceRef = React.useRef(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({ message: "", type: "" });

  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const [activeTab, setActiveTab] = useState("history"); // summary | history | types
  const [showNewLeave, setShowNewLeave] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: "", title: "" });
  const [editLeave, setEditLeave] = useState(null);
  const [detailLeaveId, setDetailLeaveId] = useState(null);
  const [detailInitial, setDetailInitial] = useState(null);
  const [editForm, setEditForm] = useState({
    leave_type_id: "",
    leave_sub_type_id: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    half_day_period: "",
    reason: "",
  });

  const historyPageSizeKey = "pageSize.leaves.history";
  const [historyPageSize, setHistoryPageSize] = useState(() => {
    const raw = localStorage.getItem(historyPageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [historyPage, setHistoryPage] = useState(1);

  const typesPageSizeKey = "pageSize.leaves.types";
  const [typesPageSize, setTypesPageSize] = useState(() => {
    const raw = localStorage.getItem(typesPageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [typesPage, setTypesPage] = useState(1);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const locale = lang === "tr" ? "tr-TR" : "en-US";
    return new Date(dateStr).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const prettyStageName = (stage) => {
    if (!stage) return "—";
    const map = {
      unit_supervisor: t("leave.stage.unitSupervisor"),
      manager: t("leave.stage.unitSupervisor"),
      company_manager: t("leave.stage.companyManager"),
      hr: t("leave.stage.hr"),
    };
    return map[stage] || stage;
  };

  const prettyStatus = (status) => {
    if (!status) return "—";
    const lower = String(status).toLowerCase();
    if (lower === "beklemede") return t("leave.status.pending");
    if (lower === "onaylandı") return t("leave.status.approved");
    if (lower === "reddedildi") return t("leave.status.rejected");
    if (lower === "atlandı") return t("leave.status.skipped");
    return status;
  };

  const stageOrder = useMemo(
    () => [
      { key: "manager_status", label: t("leave.stage.unitSupervisor") },
      { key: "company_status", label: t("leave.stage.companyManager") },
      { key: "hr_status", label: t("leave.stage.hr") },
    ],
    [t]
  );

  // Yenilemelerde (iptal, düzenleme, modal kapanışı) sayfa "Yükleniyor"a düşüp
  // açık modalları kapatmasın diye loading yalnızca ilk yüklemede açılır.
  const load = async () => {
    setError("");
    try {
      const [leavesRes, typesRes] = await Promise.all([
        apiClient.get("/leaves/my"),
        apiClient.get("/leave-types"),
      ]);
      setLeaves(Array.isArray(leavesRes.data) ? leavesRes.data : []);
      setLeaveTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      loadedOnceRef.current = true;
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || t("common.genericError");
      if (loadedOnceRef.current) setNotification({ message: msg, type: "error" });
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem(historyPageSizeKey, String(historyPageSize));
    setHistoryPage(1);
  }, [historyPageSize]);

  useEffect(() => {
    localStorage.setItem(typesPageSizeKey, String(typesPageSize));
    setTypesPage(1);
  }, [typesPageSize]);

  // --- Talep geçmişi: durum sekmeleri + arama + filtreler ---
  const [statusTab, setStatusTab] = useState("all"); // all | pending | approved | closed
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");
  const [typeSearch, setTypeSearch] = useState("");


  const tabCounts = useMemo(
    () =>
      countBy(leaves, {
        all: () => true,
        pending: (l) => isPendingStatus(l.status),
        approved: (l) => isApprovedStatus(l.status),
        closed: (l) => isClosedStatus(l.status),
      }),
    [leaves]
  );

  const filteredLeaves = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const ts = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const list = leaves.filter((l) => {
      if (statusTab === "pending" && !isPendingStatus(l.status)) return false;
      if (statusTab === "approved" && !isApprovedStatus(l.status)) return false;
      if (statusTab === "closed" && !isClosedStatus(l.status)) return false;
      if (typeFilter && String(l.leave_type_id) !== typeFilter) return false;
      const startKey = dateKey(l.start_date);
      const endKey = dateKey(l.end_date) || startKey;
      if (fromDate && endKey && endKey < fromDate) return false;
      if (toDate && startKey && startKey > toDate) return false;
      if (q) {
        const hay = `${l.leave_type_name || ""} ${l.leave_sub_type_name || ""} ${
          l.reason || ""
        } ${l.request_code || ""} LT-${String(l.id).padStart(6, "0")}`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const sorters = {
      created_desc: (a, b) => ts(b.created_at || b.start_date) - ts(a.created_at || a.start_date),
      created_asc: (a, b) => ts(a.created_at || a.start_date) - ts(b.created_at || b.start_date),
      start_desc: (a, b) => ts(b.start_date) - ts(a.start_date),
      start_asc: (a, b) => ts(a.start_date) - ts(b.start_date),
    };
    return [...list].sort(sorters[sortKey] || sorters.created_desc);
  }, [leaves, statusTab, search, typeFilter, fromDate, toDate, sortKey]);

  const hasActiveFilter = Boolean(search || typeFilter || fromDate || toDate);
  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setFromDate("");
    setToDate("");
  };

  useEffect(() => {
    setHistoryPage(1);
  }, [statusTab, search, typeFilter, fromDate, toDate, sortKey]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredLeaves.length || 0) / historyPageSize) || 1;
    if (historyPage > totalPages) setHistoryPage(totalPages);
  }, [filteredLeaves.length, historyPage, historyPageSize]);

  const filteredLeaveTypes = useMemo(() => {
    const q = typeSearch.trim().toLocaleLowerCase("tr-TR");
    if (!q) return leaveTypes;
    return leaveTypes.filter((lt) =>
      `${lt.name || ""} ${lt.description || ""}`.toLocaleLowerCase("tr-TR").includes(q)
    );
  }, [leaveTypes, typeSearch]);

  useEffect(() => {
    setTypesPage(1);
  }, [typeSearch]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredLeaveTypes.length || 0) / typesPageSize) || 1;
    if (typesPage > totalPages) setTypesPage(totalPages);
  }, [filteredLeaveTypes.length, typesPage, typesPageSize]);

  const pagedLeaves = useMemo(() => {
    const start = (historyPage - 1) * historyPageSize;
    return filteredLeaves.slice(start, start + historyPageSize);
  }, [filteredLeaves, historyPage, historyPageSize]);

  const pagedLeaveTypes = useMemo(() => {
    const start = (typesPage - 1) * typesPageSize;
    return filteredLeaveTypes.slice(start, start + typesPageSize);
  }, [filteredLeaveTypes, typesPage, typesPageSize]);

  const waitingDays = (leave) => {
    const d = new Date(leave.created_at);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  };

  const editLeaveType = useMemo(() => {
    const id = Number(editForm.leave_type_id);
    if (!Number.isFinite(id)) return null;
    return (leaveTypes || []).find((lt) => Number(lt.id) === id) || null;
  }, [leaveTypes, editForm.leave_type_id]);
  const editIsHourly = Boolean(editLeaveType?.is_duration_flexible);

  // Blob olarak dönen hata gövdesini (PDF endpoint'leri) okuyup bildirim olarak göster.
  const reportBlobError = async (err) => {
    let message = err?.response?.data?.msg || err?.message || t("common.genericError");
    try {
      const blob = err?.response?.data;
      if (blob && typeof blob.text === "function") {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          message = parsed.msg || parsed.error || text;
        } catch (_) {
          message = text || message;
        }
      }
    } catch (_) {
      // ignore
    }
    setNotification({ message, type: "error" });
  };

  const fetchPdfBlobUrl = async (id) => {
    const response = await apiClient.get(`/leaves/${id}/document`, { responseType: "blob" });
    return window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  };

  const fetchReportBlobUrl = async (id) => {
    const response = await apiClient.get(`/leaves/${id}/report-pdf`, { responseType: "blob" });
    return window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  };

  const downloadPdf = async (id) => {
    try {
      const blobUrl = await fetchPdfBlobUrl(id);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `leave-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      await reportBlobError(err);
    }
  };

  const downloadReportPdf = async (id) => {
    try {
      const blobUrl = await fetchReportBlobUrl(id);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `leave-${id}-report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      await reportBlobError(err);
    }
  };

  const previewReportPdf = async (id) => {
    try {
      const blobUrl = await fetchReportBlobUrl(id);
      setPdfPreview({
        open: true,
        url: blobUrl,
        title: `${t("leave.reportPdf.title")}: LT-${String(id).padStart(6, "0")}`,
      });
    } catch (err) {
      await reportBlobError(err);
    }
  };

  const previewPdf = async (id) => {
    try {
      const blobUrl = await fetchPdfBlobUrl(id);
      setPdfPreview({
        open: true,
        url: blobUrl,
        title: `${t("leave.document.ref")}: LT-${String(id).padStart(6, "0")}`,
      });
    } catch (err) {
      await reportBlobError(err);
    }
  };

  const closePreview = () => {
    if (pdfPreview.url) window.URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview({ open: false, url: "", title: "" });
  };

  const cancelLeave = async (leave) => {
    if (!leave?.id) return;
    const ok = await confirm(t("leave.cancelConfirm"));
    if (!ok) return;
    try {
      await apiClient.delete(`/leaves/${leave.id}`);
      setNotification({ message: t("leave.cancelSuccess"), type: "success" });
      await load();
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || err.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const isEditableByUser = (leave) => {
    if (!leave) return false;
    if (leave.pending_edit_payload) return false;
    if (String(leave.status || "").trim().toLowerCase() !== "beklemede") return false;
    const stageStatuses = [leave.manager_status, leave.company_status, leave.hr_status];
    const anyApproved = stageStatuses.some(
      (s) => String(s || "").trim().toLowerCase() === "onaylandı"
    );
    if (anyApproved) return false;
    const start = parseDateOnly(leave.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cutoff = new Date(today.getTime());
    cutoff.setDate(cutoff.getDate() - OWNER_EDIT_PAST_DAYS);
    return start >= cutoff;
  };

  const openEdit = (leave) => {
    setEditLeave(leave);
    setEditForm({
      leave_type_id: leave?.leave_type_id ? String(leave.leave_type_id) : "",
      leave_sub_type_id: leave?.leave_sub_type_id ? String(leave.leave_sub_type_id) : "",
      start_date: String(leave?.start_date || "").slice(0, 10),
      end_date: String(leave?.end_date || "").slice(0, 10),
      start_time: leave?.start_time ? String(leave.start_time).slice(0, 5) : "",
      end_time: leave?.end_time ? String(leave.end_time).slice(0, 5) : "",
      half_day_period: leave?.half_day_period ? String(leave.half_day_period) : "",
      reason: leave?.reason || "",
    });
  };

  const closeEdit = () => {
    setEditLeave(null);
    setEditForm({
      leave_type_id: "",
      leave_sub_type_id: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      half_day_period: "",
      reason: "",
    });
  };

  const submitEdit = async () => {
    if (!editLeave) return;
    const leaveTypeId = Number(editForm.leave_type_id);
    if (!Number.isFinite(leaveTypeId) || !editForm.start_date || !editForm.reason?.trim()) {
      setNotification({ message: t("leave.edit.required"), type: "error" });
      return;
    }

    const selectedType = (leaveTypes || []).find((lt) => Number(lt.id) === leaveTypeId);
    const isHourly = Boolean(selectedType?.is_duration_flexible);
    const isAnnual = String(selectedType?.name || "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .startsWith("yıllık izin");
    const halfDayPeriod = String(editForm.half_day_period || "").trim().toUpperCase();
    const isHalfDay = !isHourly && isAnnual && (halfDayPeriod === "AM" || halfDayPeriod === "PM");
    if (isHourly) {
      if (!editForm.start_time || !editForm.end_time) {
        setNotification({ message: t("leave.edit.timeRequired"), type: "error" });
        return;
      }
    } else if (!isHalfDay && !editForm.end_date) {
      setNotification({ message: t("leave.edit.endDateRequired"), type: "error" });
      return;
    }

    const payload = {
      leave_type_id: leaveTypeId,
      leave_sub_type_id: editForm.leave_sub_type_id ? Number(editForm.leave_sub_type_id) : null,
      start_date: editForm.start_date,
      end_date: isHourly || isHalfDay ? editForm.start_date : editForm.end_date,
      reason: editForm.reason,
      half_day_period: isHalfDay ? halfDayPeriod : null,
      ...(isHourly ? { start_time: editForm.start_time, end_time: editForm.end_time } : {}),
    };

    try {
      const response = await apiClient.put(`/leaves/${editLeave.id}`, payload);
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === editLeave.id ? { ...l, ...updated } : l)));
      closeEdit();

      const warningMsg =
        Array.isArray(updated?.warnings) && updated.warnings[0]?.message
          ? updated.warnings[0].message
          : "";
      if (warningMsg) {
        setNotification({ message: warningMsg, type: "warning" });
      } else {
        setNotification({ message: t("leave.edit.resubmittedSuccess"), type: "success" });
      }
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || err.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const annualBalance = useMemo(() => {
    return Number(user?.total_leave_quota_days || 0);
  }, [user]);

  const approvedLeaves = useMemo(
    () => leaves.filter((l) => l.status === "Onaylandı"),
    [leaves]
  );

  const [holidayKeys, setHolidayKeys] = useState(() => new Set());
  useEffect(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 364);
    let cancelled = false;
    apiClient
      .get("/public-holidays", { params: { start: localKey(from), end: localKey(now) } })
      .then((res) => {
        if (cancelled) return;
        setHolidayKeys(new Set((res.data || []).map((h) => String(h.date).slice(0, 10))));
      })
      .catch(() => {
        if (!cancelled) setHolidayKeys(new Set()); // tatil bilgisi yoksa hafta sonu hariç sayım
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const usage = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    const weekStart = new Date(end.getTime());
    weekStart.setDate(weekStart.getDate() - 6);
    const monthStart = new Date(end.getTime());
    monthStart.setDate(monthStart.getDate() - 29);
    const yearStart = new Date(end.getTime());
    yearStart.setDate(yearStart.getDate() - 364);

    const calcForWindow = (windowStart) => {
      let days = 0;
      let hours = 0;
      for (const l of approvedLeaves) {
        if (l.total_duration_hours != null) {
          const d = parseDateOnly(l.start_date);
          if (d >= windowStart && d <= end) hours += Number(l.total_duration_hours || 0);
        } else if (l.start_date && l.end_date) {
          const s = parseDateOnly(l.start_date);
          const e = parseDateOnly(l.end_date);
          days += overlapBusinessDays(s, e, windowStart, end, holidayKeys);
        }
      }
      return { days, hours };
    };

    return {
      week: calcForWindow(weekStart),
      month: calcForWindow(monthStart),
      year: calcForWindow(yearStart),
    };
  }, [approvedLeaves, holidayKeys]);

  if (loading) return <div className="leaves-page">{t("common.loading")}</div>;
  if (error) return <div className="leaves-page error">{t("common.error")}: {error}</div>;

  return (
    <div className="leaves-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="leaves-header">
        <div>
          <h2>{t("nav.leaves")}</h2>
          <p className="muted">{t("leavesPage.subtitle")}</p>
        </div>
        <button className="primary" onClick={() => setShowNewLeave(true)}>
          {t("leave.newRequest")}
        </button>
      </div>

      <div className="summary-grid summary-grid--4">
        <div className="summary-card">
          <div className="summary-title">{t("dashboard.leaveBalance")}</div>
          <div className="summary-value">
            {annualBalance} {t("leave.summary.days")}
          </div>
        </div>
        <button
          type="button"
          className={`summary-card summary-card--link${tabCounts.pending > 0 ? " attention" : ""}`}
          onClick={() => {
            setActiveTab("history");
            setStatusTab("pending");
          }}
        >
          <div className="summary-title">{t("leavesPage.pendingRequests")}</div>
          <div className="summary-value">{tabCounts.pending}</div>
        </button>
        <div className="summary-card">
          <div className="summary-title">{t("leavesPage.last30")}</div>
          <div className="summary-value">
            {usage.month.days} {t("leave.summary.days")}
            {usage.month.hours > 0 ? ` / ${usage.month.hours.toFixed(1)}h` : ""}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-title">{t("leavesPage.last365")}</div>
          <div className="summary-value">
            {usage.year.days} {t("leave.summary.days")}
            {usage.year.hours > 0 ? ` / ${usage.year.hours.toFixed(1)}h` : ""}
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          {t("nav.myLeaves")}
        </button>
        <button
          className={`tab ${activeTab === "types" ? "active" : ""}`}
          onClick={() => setActiveTab("types")}
        >
          {t("nav.leaveTypes")}
        </button>
      </div>

      {activeTab === "history" && (
        <div className="panel">
          {leaves.length === 0 ? (
            <EmptyState
              message={t("leave.myEmpty")}
              action={
                <button className="primary" type="button" onClick={() => setShowNewLeave(true)}>
                  {t("leave.newRequest")}
                </button>
              }
            />
          ) : (
            <>
              <TabBar
                value={statusTab}
                onChange={setStatusTab}
                ariaLabel={t("nav.myLeaves")}
                tabs={[
                  { key: "all", label: t("leavesPage.tabs.all"), count: tabCounts.all },
                  { key: "pending", label: t("leavesPage.tabs.pending"), count: tabCounts.pending, attention: true },
                  { key: "approved", label: t("leavesPage.tabs.approved"), count: tabCounts.approved },
                  { key: "closed", label: t("leavesPage.tabs.closed"), count: tabCounts.closed },
                ]}
              />
              <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t("leavesPage.filters.search")}
                />
                <select
                  className="leaves-filter-input lt-input"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label={t("leavesPage.filters.type")}
                >
                  <option value="">{t("leavesPage.filters.allTypes")}</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={String(lt.id)}>
                      {lt.name}
                    </option>
                  ))}
                </select>
                <FilterField label={t("leavesPage.filters.from")}>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </FilterField>
                <FilterField label={t("leavesPage.filters.to")}>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </FilterField>
                <select
                  className="leaves-filter-input lt-input"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  aria-label={t("leavesPage.filters.sort")}
                >
                  <option value="created_desc">{t("leavesPage.sort.createdDesc")}</option>
                  <option value="created_asc">{t("leavesPage.sort.createdAsc")}</option>
                  <option value="start_desc">{t("leavesPage.sort.startDesc")}</option>
                  <option value="start_asc">{t("leavesPage.sort.startAsc")}</option>
                </select>
              </FilterBar>
              {filteredLeaves.length === 0 ? (
                <EmptyState message={t("leavesPage.emptyFiltered")} />
              ) : (
                <>
                  <PaginationControls
                    totalItems={filteredLeaves.length}
                    page={historyPage}
                    pageSize={historyPageSize}
                    onPageChange={setHistoryPage}
                    onPageSizeChange={setHistoryPageSize}
                  />
              <div className="history-list">
                {pagedLeaves.map((l) => (
                  <div key={l.id} className={`history-item status-${String(l.status).toLowerCase()}`}>
                    <div className="history-main">
                      <div className="history-title">
                        {l.leave_type_name}
                        {l.leave_sub_type_name ? ` (${l.leave_sub_type_name})` : ""}
                      </div>
                      <div className="history-meta">
                        <span>
                          {t("leave.fields.start")}: {formatDate(l.start_date)}
                        </span>
                        <span>
                          {t("leave.fields.end")}: {formatDate(l.end_date)}
                        </span>
                        <span className="pill">{prettyStatus(l.status)}</span>
                        {l.status === "Beklemede" && l.current_stage && (
                          <span className="pill pending">
                            {t("leave.nextStage")}: {prettyStageName(l.current_stage)}
                          </span>
                        )}
                        {l.status === "Beklemede" && waitingDays(l) != null && (
                          <span className={`pill${waitingDays(l) >= 3 ? " danger" : ""}`}>
                            {waitingDays(l)} {t("leavesPage.waitingDays")}
                          </span>
                        )}
                        {l.status === "Onaylandı" && (
                          <>
                            <span className="pill">
                              {t("leave.document.ref")}: LT-{String(l.id).padStart(6, "0")}
                            </span>
                            <button
                              className="pill action"
                              type="button"
                              onClick={() => previewPdf(l.id)}
                            >
                              {t("leave.document.previewShort")}
                            </button>
                            <button
                              className="pill action"
                              type="button"
                              onClick={() => downloadPdf(l.id)}
                            >
                              {t("leave.document.downloadShort")}
                            </button>
                          </>
                        )}
                        {String(l.leave_type_name || "").trim().toLocaleLowerCase("tr-TR") === "rapor" &&
                          l.has_report_pdf && (
                            <>
                              <span className="pill">{t("leave.reportPdf.badge")}</span>
                              <button
                                className="pill action"
                                type="button"
                                onClick={() => previewReportPdf(l.id)}
                              >
                                {t("leave.reportPdf.previewShort")}
                              </button>
                              <button
                                className="pill action"
                                type="button"
                                onClick={() => downloadReportPdf(l.id)}
                              >
                                {t("leave.reportPdf.downloadShort")}
                              </button>
                            </>
                          )}
                        <button
                          className="pill action"
                          type="button"
                          onClick={() => {
                            setDetailInitial(l);
                            setDetailLeaveId(l.id);
                          }}
                        >
                          {t("admin.leaves.details")}
                        </button>
                        {isEditableByUser(l) && (
                          <button className="pill action" type="button" onClick={() => openEdit(l)}>
                            {t("common.edit")}
                          </button>
                        )}
                        {String(l.status || "") === "Beklemede" && (
                          <button className="pill action danger" type="button" onClick={() => cancelLeave(l)}>
                            {t("leave.actions.cancel")}
                          </button>
                        )}
                      </div>

                      <div className="stage-grid">
                        {stageOrder.map((s) => (
                          <div key={s.key} className="stage-chip">
                            <span className="stage-label">{s.label}</span>
                            <span className="stage-value">{prettyStatus(l[s.key])}</span>
                          </div>
                        ))}
                      </div>

                      {l.status === "Reddedildi" && l.rejection_reason && (
                        <div className="reject-reason">
                          <strong>{t("leave.status.rejected")}:</strong> {l.rejection_reason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
                  <PaginationControls
                    totalItems={filteredLeaves.length}
                    page={historyPage}
                    pageSize={historyPageSize}
                    onPageChange={setHistoryPage}
                    onPageSizeChange={setHistoryPageSize}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "types" && (
        <div className="panel">
          {leaveTypes.length === 0 ? (
            <EmptyState message={t("leaveTypes.empty")} />
          ) : (
            <div className="table-wrap">
              <FilterBar showReset={Boolean(typeSearch)} onReset={() => setTypeSearch("")}>
                <SearchInput
                  value={typeSearch}
                  onChange={setTypeSearch}
                  placeholder={t("leavesPage.filters.searchTypes")}
                />
              </FilterBar>
              {filteredLeaveTypes.length === 0 && (
                <EmptyState message={t("leavesPage.emptyFiltered")} />
              )}
              <PaginationControls
                totalItems={filteredLeaveTypes.length}
                page={typesPage}
                pageSize={typesPageSize}
                onPageChange={setTypesPage}
                onPageSizeChange={setTypesPageSize}
              />
              <table className="types-table">
                <thead>
                  <tr>
                    <th>{t("leaveTypes.columns.name")}</th>
                    <th>{t("leaveTypes.columns.description")}</th>
                    <th>{t("leaveTypes.columns.affectsBalance")}</th>
                    <th>{t("leaveTypes.columns.type")}</th>
                    <th>{t("leaveTypes.columns.defaultDuration")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedLeaveTypes.map((lt) => (
                    <tr key={lt.id}>
                      <td>{lt.name}</td>
                      <td>{lt.description || "-"}</td>
                      <td>{lt.affects_balance ? t("common.yes") : t("common.no")}</td>
                      <td>
                        {lt.is_duration_flexible
                          ? t("leaveTypes.duration.hourly")
                          : t("leaveTypes.duration.daily")}
                      </td>
                      <td>
                        {lt.is_duration_flexible
                          ? lt.default_duration_hours ?? "-"
                          : lt.default_duration_days ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                totalItems={filteredLeaveTypes.length}
                page={typesPage}
                pageSize={typesPageSize}
                onPageChange={setTypesPage}
                onPageSizeChange={setTypesPageSize}
              />
            </div>
          )}
        </div>
      )}

      {showNewLeave && (
        <div className="modal-overlay" onMouseDown={() => setShowNewLeave(false)}>
          <div className="modal-content wide" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("leave.createTitle")}</h3>
              <button className="ghost" onClick={() => setShowNewLeave(false)}>
                {t("common.close")}
              </button>
            </div>
            <LeaveForm
              redirectTo={null}
              onLeaveCreated={async () => {
                setShowNewLeave(false);
                await load();
              }}
            />
          </div>
        </div>
      )}

      {pdfPreview.open && (
        <div className="modal-overlay" onMouseDown={closePreview}>
          <div className="modal-content pdf" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{pdfPreview.title}</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="ghost"
                  type="button"
                  onClick={() => {
                    if (!pdfPreview.url) return;
                    const w = window.open(pdfPreview.url, "_blank", "noopener,noreferrer");
                    if (w) w.focus();
                  }}
                >
                  {t("leave.document.openNewTab")}
                </button>
                <button className="ghost" type="button" onClick={closePreview}>
                  {t("common.close")}
                </button>
              </div>
            </div>
            <iframe className="pdf-frame" src={pdfPreview.url} title={pdfPreview.title} />
          </div>
        </div>
      )}

      {editLeave && (
        <div className="modal-overlay" onMouseDown={closeEdit}>
          <div className="modal-content wide" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("leave.edit.modalTitle")}</h3>
              <button className="ghost" type="button" onClick={closeEdit}>
                {t("common.close")}
              </button>
            </div>

            <div className="edit-grid">
              <label>
                {t("leave.form.leaveType")}
                <select
                  value={editForm.leave_type_id}
                  onChange={(e) => setEditForm((p) => ({ ...p, leave_type_id: e.target.value }))}
                >
                  <option value="">{t("leave.form.leaveTypeSelect")}</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("leave.form.startDate")}
                <input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                />
              </label>

              {!editIsHourly && (
                <label>
                  {t("leave.form.endDate")}
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </label>
              )}

              {editIsHourly && (
                <>
                  <label>
                    {t("leave.form.startTime")}
                    <input
                      type="time"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm((p) => ({ ...p, start_time: e.target.value }))}
                    />
                  </label>
                  <label>
                    {t("leave.form.endTime")}
                    <input
                      type="time"
                      value={editForm.end_time}
                      onChange={(e) => setEditForm((p) => ({ ...p, end_time: e.target.value }))}
                    />
                  </label>
                </>
              )}

              <label className="span-2">
                {t("leave.form.reason")}
                <textarea
                  rows={3}
                  value={editForm.reason}
                  onChange={(e) => setEditForm((p) => ({ ...p, reason: e.target.value }))}
                />
              </label>
            </div>

            <div className="modal-footer">
              <button className="ghost" type="button" onClick={closeEdit}>
                {t("common.cancel")}
              </button>
              <button className="primary" type="button" onClick={submitEdit}>
                {t("leave.actions.update")}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailLeaveId && (
        <LeaveDetailsModal
          leaveId={detailLeaveId}
          initial={detailInitial}
          onClose={() => {
            setDetailLeaveId(null);
            setDetailInitial(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default LeavesPage;
